import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { AlertType, TextButton, TextInput, Typography } from '../../../components';
import {
  IntegrationConnectionStatus,
  IntegrationService,
  SalesloftAuth,
  SalesloftAuthMethod,
  TextColor,
} from '../../../types';
import { useAppDispatch, useHandleApiResponse, useToast } from '../../../hooks';
import { updateOrganization } from '../../../redux/reducers';
import { useSubmitSalesloftAPIKeyMutation } from '../../../services';
import { SECRET_KEY_HIDDEN_CHARS_NUMBER } from '../IntegrationItemLayout';

const CONNECT_SALESLOFT_ERROR_MSG = 'Connection to Salesloft failed. Please try again later.';

interface ConnectSalesloftApiKeyProps {
  connectionStatus: IntegrationConnectionStatus;
  setConnectionStatus: Dispatch<SetStateAction<IntegrationConnectionStatus>>;
  salesloftAuth?: SalesloftAuth;
}

const ConnectSalesloftApiKey = ({
  salesloftAuth,
  connectionStatus,
  setConnectionStatus,
}: ConnectSalesloftApiKeyProps) => {
  const [apiKeyInput, setApiKeyInput] = useState('');

  const isSalesloftConnected = connectionStatus === IntegrationConnectionStatus.CONNECTED;
  const { apiKeyLast4 } = salesloftAuth || {};

  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const [submitSalesloftAPIKey, { isLoading: isSubmitting }] = useSubmitSalesloftAPIKeyMutation();

  const handleApiResponse = useHandleApiResponse();

  // Reset the inputs when the connection is disconnected.
  useEffect(() => {
    if (connectionStatus === IntegrationConnectionStatus.DISCONNECTED) {
      setApiKeyInput('');
    }
  }, [connectionStatus]);

  useEffect(() => {
    if (apiKeyLast4) {
      setApiKeyInput('*'.repeat(SECRET_KEY_HIDDEN_CHARS_NUMBER) + apiKeyLast4);
    }
  }, [apiKeyLast4]);

  // Handle the success response
  const onSuccess = useCallback(() => {
    dispatch(
      updateOrganization({
        salesloftAuth: {
          service: IntegrationService.SALESLOFT,
          expiresAt: null,
          authMethod: SalesloftAuthMethod.API_KEY,
          apiKeyLast4: apiKeyInput.slice(-4),
        },
      })
    );
  }, [dispatch, apiKeyInput]);

  const onError = useCallback(() => {
    setConnectionStatus(IntegrationConnectionStatus.FAILED);
  }, []);

  const handleSubmitSalesloftAPIKey = useCallback(async () => {
    try {
      const response = await submitSalesloftAPIKey({
        service: IntegrationService.SALESLOFT,
        apiKey: apiKeyInput,
      });
      handleApiResponse({
        response,
        errorMsg: CONNECT_SALESLOFT_ERROR_MSG,
        successMsg: 'Successfully connected to Salesloft',
        onError,
        onSuccess,
      });
    } catch (error) {
      onError();
      showToast({ message: CONNECT_SALESLOFT_ERROR_MSG, type: AlertType.ERROR });
    }
  }, [submitSalesloftAPIKey, handleApiResponse, onSuccess, onError, showToast]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Typography color={TextColor.SECONDARY}>API Key</Typography>
        <TextInput
          value={apiKeyInput}
          onChange={(e) => setApiKeyInput(e.target.value)}
          disabled={isSalesloftConnected}
        />
      </div>
      <TextButton
        disabled={isSalesloftConnected || !apiKeyInput}
        text="Connect"
        onClick={handleSubmitSalesloftAPIKey}
        loading={isSubmitting}
      />
    </div>
  );
};

export default ConnectSalesloftApiKey;
